import React, {useEffect, useState} from "react"
import {useSessionCookie} from "../hooks/useSessionCookie";
import {createContext} from "react";
import {UserFormData} from "../models/user";
import {useSearchParams} from "react-router-dom";
import _ from "lodash"
import {useAsync} from "../hooks/useAsyncResult";
import {updatePassword} from "../services/api/security";
import {updateProfile} from "../services/api/user";
import getUpdatedFields from "../utils/getUpdatedFields";
import {useFeedback} from "../hooks/useFeedback";

export type SettingsContextState = {
    userFormData?: UserFormData,
    password?: {
        oldPassword?: string,
        newPassword?: string,
        confirmNew?: string,
    },
    onPasswordInfoChange?: (password: {
        oldPassword?: string,
        newPassword?: string,
        confirmNew?: string,
    }) => void,
    onSaveChanges?: () => void,
    onUserFormDataChange?: (key: keyof UserFormData, value: any) => void,
    canSaveChanges?: boolean,
    savingChanges?: boolean,
    setFieldsValid?: (valid?: boolean) => void,
}

export const ISettingsContext = createContext<SettingsContextState>({})

const SettingsContext = (props: { children }) => {
    const {userSession, setUserSession} = useSessionCookie()
    const feedback = useFeedback()
    const [userFormData, setUserFormData] = useState<UserFormData>(userSession?.['currentUser'] || {})
    const [searchParams] = useSearchParams()
    const [password, setPassword] = useState<{ oldPassword?: string, newPassword?: string, confirmNew?: string }>({})
    const [canSaveChanges, setCanSaveChanges] = useState(false)
    const updatePasswordAsyncResult = useAsync(updatePassword, true, true, false)
    const updateProfileAsyncResult = useAsync(updateProfile, true, true, false)
    const personalInfoFieldValidation: {
        [key in keyof UserFormData]: {
            required: boolean,
            message?: string,
            check?: boolean
        }
    } = {
        name: {
            check: new RegExp(/^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,}$/).test(userFormData?.name),
            message: "Please enter valid name",
            required: true
        },
        email: {
            required: true,
            message: "Please enter email",
            check: new RegExp(/^[^\s@]+@[^\s@]+\.[^\s@]+$/).test(userFormData?.email)
        },
    }

    const securityFieldValidation: {
        [key in "oldPassword" | "newPassword" | "confirmNew"]?: {
            required: boolean,
            message?: string,
            check?: boolean
        }
    } = {
        oldPassword: {
            check: password?.oldPassword?.length >= 6,
            message: "invalidPasswordFieldMessage",
            required: true
        },
        newPassword: {
            check: password?.newPassword?.length >= 6,
            message: "invalidPasswordFieldMessage",
            required: true
        },
        confirmNew: {
            check: password?.confirmNew === password?.newPassword,
            message: "invalidConfirmPasswordMessage",
            required: true
        },
    }

    const fieldValidationMapper = {
        profile: personalInfoFieldValidation,
        security: securityFieldValidation,
    }


    const handleSubmit = () => {
        if (searchParams.get("section") === 'profile') {
            const updatedFields: UserFormData = getUpdatedFields(userSession?.['currentUser'] || {}, userFormData)
            if (updatedFields['avatar'])
                updatedFields['avatar'] = updatedFields['avatar'].split(",")[1]

            updateProfileAsyncResult.execute(updatedFields)
        } else if (searchParams.get("section") === 'security')
            updatePasswordAsyncResult.execute(password)
    }

    const handleFormDataChange = (key: keyof UserFormData, value: any) => {
        setUserFormData({...userFormData, [key]: value})
    }

    const validateForm = () => {
        if (searchParams.get("section")) {
            const fieldValidation = fieldValidationMapper[searchParams.get("section")]
            if (!fieldValidation)
                return

            const fields = Object.keys(fieldValidation)
            let isUpdateValid = true

            if (searchParams.get("section") === 'profile') {
                const originalFields = userSession?.['currentUser'] || {}
                let originalSectionFields = {}, updatedSectionFields = {}

                fields.forEach(field => {
                    originalSectionFields[field] = originalFields[field]
                    updatedSectionFields = userFormData[field]
                })

                isUpdateValid = !_.isEqual(userFormData, originalFields) && !_.isEqual(originalSectionFields, updatedSectionFields)
                setCanSaveChanges(isUpdateValid && Object.keys(fieldValidation)?.every(value =>
                    fieldValidation[value].required ? !!userFormData[value] && fieldValidation[value].check : !userFormData[value] || fieldValidation[value].check))
            } else if (searchParams.get("section") === 'security')
                setCanSaveChanges(isUpdateValid && Object.keys(fieldValidation)?.every(value =>
                    fieldValidation[value].required ? !!password[value] && fieldValidation[value].check : !password[value] || fieldValidation[value].check))


        }
    }

    const handleUpdatePasswordSuccess = () => {
        feedback.showFeedback("Password successfully updated", "success")
        setPassword({
            newPassword: "",
            oldPassword: "",
            confirmNew: ""
        })
    }

    const handleUpdateProfileSuccess = () => {
        feedback.showFeedback("accountUpdateSuccessMessage", "success")
        setUserSession({
            currentUser: {
                ...updateProfileAsyncResult.payload
            },
            token: userSession.token,
            expiresAt: userSession.expiresAt,
            merchantStore: userSession.merchantStore,
        }, new Date(userSession.expiresAt))
        setCanSaveChanges(false)
    }

    useEffect(() => {
        if (userFormData)
            validateForm()
    }, [userFormData, searchParams, password])

    useEffect(() => {
        if (updateProfileAsyncResult.isSuccess)
            handleUpdateProfileSuccess()

        if (updatePasswordAsyncResult.isSuccess)
            handleUpdatePasswordSuccess()
    }, [updatePasswordAsyncResult.isSuccess, updateProfileAsyncResult.isSuccess])


    return <ISettingsContext.Provider
        value={{
            canSaveChanges,
            onUserFormDataChange: handleFormDataChange,
            userFormData,
            password,
            onSaveChanges: handleSubmit,
            setFieldsValid: setCanSaveChanges,
            onPasswordInfoChange: setPassword,
            savingChanges: updatePasswordAsyncResult.loading || updateProfileAsyncResult.loading,
        }}>
        {props.children}
    </ISettingsContext.Provider>

}


export default SettingsContext